import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from './ui/Icon';
import { useIsMobile } from '../utils/mobileDetection';

const JournalLanding = ({ petName, angelPet, onTileClick }) => {
  const isMobile = useIsMobile();
  
  // Floating particles data
  const particles = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    delay: i * 0.5,
    duration: 8 + (i % 3) * 2,
    initialX: Math.random() * 100,
    initialY: Math.random() * 100,
    size: Math.random() * 0.5 + 0.3,
    iconName: ['sparkle', 'star', 'sparkle', 'star'][i % 4]
  }));

  const journalTiles = [
    { icon: <Icon name="heart" size="lg" color="memories" />, label: "Healing Journey", action: "writeToYourPet" },
    { icon: <Icon name="sparkle" size="lg" color="primary" />, label: "Reflect on Today", action: "reflectOnToday" }
  ];

  // Configure motion props for tiles based on device type
  const getTileMotionProps = () => {
    if (isMobile) {
      // Mobile: Only tap feedback, no hover
      return {
        whileTap: { scale: 0.95 },
        transition: { duration: 0.1, ease: "easeOut" }
      };
    } else {
      // Desktop: Both hover and tap feedback
      return {
        whileHover: { scale: 1.05, y: -5 },
        whileTap: { scale: 0.95 },
        transition: { duration: 0.2, ease: "easeOut" }
      };
    }
  };

  const Tile = ({ icon, label, action }) => (
    <motion.div
      {...getTileMotionProps()}
      className="bg-white/30 backdrop-blur-md rounded-xl p-3 shadow-lg hover:shadow-xl transition-all duration-300 text-center cursor-pointer border border-white/20"
      onClick={() => onTileClick(action)}
    >
      <div className="text-2xl mb-1">{icon}</div>
      <p className="text-sm text-slate-700 font-normal">{label}</p>
    </motion.div>
  );

  const TypingText = ({ text }) => (
    <p className="text-center text-sm text-gray-700 italic leading-relaxed">
      "{text}"
      <motion.span
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
        className="ml-1"
      >
        |
      </motion.span>
    </p>
  );

  return (
    <div className="relative min-h-screen flex flex-col items-center px-4 py-4 overflow-hidden">
      {/* Background Clouds */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.img
          src="/assets/cloud1.png"
          alt=""
          className="absolute w-32 h-20 opacity-20"
          style={{ top: '10%', left: '10%' }}
          animate={{
            x: [0, 30, 0],
            y: [0, -10, 0],
            rotate: [0, 2, -2, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.img
          src="/assets/cloud1.png"
          alt=""
          className="absolute w-28 h-16 opacity-15"
          style={{ top: '70%', right: '15%' }}
          animate={{
            x: [0, -20, 0],
            y: [0, 15, 0],
            rotate: [0, -1, 1, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 10
          }}
        />
        <motion.img
          src="/assets/cloud1.png"
          alt=""
          className="absolute w-24 h-14 opacity-10"
          style={{ top: '30%', right: '5%' }}
          animate={{
            x: [0, 25, 0],
            y: [0, -20, 0],
            rotate: [0, 3, -3, 0]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
        />
      </div>

      {/* Floating Particles */}
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          initial={{ 
            opacity: 0, 
            x: `${particle.initialX}vw`, 
            y: `${particle.initialY}vh`,
            scale: 0
          }}
          animate={{
            opacity: [0, 0.6, 0],
            x: [`${particle.initialX}vw`, `${particle.initialX + 20}vw`, `${particle.initialX - 10}vw`],
            y: [`${particle.initialY}vh`, `${particle.initialY - 30}vh`, `${particle.initialY - 60}vh`],
            scale: [0, particle.size, 0],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut"
          }}
          className="absolute pointer-events-none"
          style={{ zIndex: 1 }}
        >
          <Icon name={particle.iconName} size="sm" color="primary" />
        </motion.div>
      ))}

      {/* Gentle rainbow gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/10 via-pink-100/10 to-blue-100/10 pointer-events-none" />

      {/* Angel Pet Avatar + Speech Bubble (Higher Up) */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="flex items-center justify-center mb-12 relative max-w-md mx-auto mt-8"
      >
        {/* Angel Pet on the Left */}
        <div className="flex-shrink-0 mr-4">
          {angelPet ? (
            <motion.img
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              src={angelPet}
              alt="Angel Pet"
              className="w-20 h-20 rounded-full shadow-xl border-4 border-white/30 backdrop-blur-sm"
            />
          ) : (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="w-20 h-20 rounded-full shadow-xl border-4 border-white/30 backdrop-blur-sm bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center"
            >
              <Icon name="heart" size="lg" color="memories" />
            </motion.div>
          )}
        </div>
        
        {/* Speech Bubble on the Right */}
        <motion.div
          initial={{ opacity: 0, x: 20, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="relative flex-1 max-w-xs"
        >
          {/* Speech bubble */}
          <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg border border-white/40">
            {/* Speech bubble tail pointing LEFT to angel pet */}
            <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white/90 rotate-45 border-l border-b border-white/40"></div>
            
            {/* Magical glow around bubble */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-200/20 via-pink-200/20 to-blue-200/20 rounded-2xl blur-sm animate-pulse"></div>
            
            {/* Message text */}
            <p className="relative z-10 text-left text-sm text-gray-700 font-medium leading-relaxed">
              <span className="text-purple-600 font-semibold">"{petName || 'Your beloved pet'}"</span> is with you. Ready to listen.
            </p>
            
            {/* Subtle sparkles inside bubble */}
            <div className="absolute top-1 right-2 text-purple-300/70 animate-pulse">
              <Icon name="sparkle" size="xs" color="#6B46C1" />
            </div>
            <div className="absolute bottom-1 right-3 text-pink-300/70 animate-pulse" style={{ animationDelay: '0.5s' }}>
              <Icon name="sparkle" size="xs" color="#BE185D" />
            </div>
          </div>
          
          {/* Floating sparkles around the speech bubble */}
          <motion.div
            animate={{ 
              opacity: [0.3, 0.8, 0.3],
              y: [0, -5, 0],
              x: [0, 2, 0]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="absolute -top-3 -right-3"
          >
            <Icon name="sparkle" size="xs" color="#CA8A04" />
          </motion.div>
          
          <motion.div
            animate={{ 
              opacity: [0.4, 0.9, 0.4],
              y: [0, -3, 0],
              x: [0, -2, 0]
            }}
            transition={{ 
              duration: 2.5, 
              repeat: Infinity, 
              ease: "easeInOut",
              delay: 1
            }}
            className="absolute -top-2 right-1"
          >
            <Icon name="sparkle" size="xs" color="#BE185D" />
          </motion.div>
          
          <motion.div
            animate={{ 
              opacity: [0.2, 0.7, 0.2],
              y: [0, -4, 0],
              x: [0, 1, 0]
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity, 
              ease: "easeInOut",
              delay: 0.5
            }}
            className="absolute -bottom-3 -right-2"
          >
            <Icon name="star" size="xs" color="#93c5fd" />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Journal Entry Tiles (More Space Above) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7 }}
        className="grid grid-cols-2 gap-4 w-full max-w-md mb-4 mt-4"
      >
        {journalTiles.map((tile, index) => (
          <motion.div
            key={tile.action}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
          >
            <Tile {...tile} />
          </motion.div>
        ))}
      </motion.div>

      {/* Paper Image */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0 }}
        className="flex justify-center mb-4"
      >
        <motion.img
          src="/assets/paper.png"
          alt="Paper"
          className="w-64 h-auto opacity-80"
          {...(isMobile ? {
            whileTap: { scale: 0.98 },
            transition: { duration: 0.1 }
          } : {
            whileHover: { scale: 1.02 },
            whileTap: { scale: 0.98 },
            transition: { duration: 0.3 }
          })}
        />
      </motion.div>
    </div>
  );
};

export default JournalLanding; 